import React, { useState } from "react";
import {
  FaStar,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaSlidersH,
  FaUpload,
} from "react-icons/fa";

const FormPreview = ({ template }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white p-6 rounded shadow-lg max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-blue-600 mb-6">
          {template.template_name}
        </h2>
        {template.categories.map((category) => (
          <div key={category.category_id} className="mb-8">
            <h3
              className="text-2xl font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center gap-2 cursor-pointer"
              onClick={() => toggleCategory(category.category_id)}
            >
              {category.category_name}
              {expandedCategories[category.category_id] ? (
                <FaChevronUp className="ml-auto" />
              ) : (
                <FaChevronDown className="ml-auto" />
              )}
            </h3>
            {expandedCategories[category.category_id] && (
              <div className="space-y-6">
                {category.questions.map((question) => (
                  <QuestionWithDependencies
                    key={question.question_id}
                    question={question}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const QuestionWithDependencies = ({ question }) => {
  const [currentValue, setCurrentValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState({});

  const handleYesNoChange = (value) => {
    setCurrentValue(value);
    if (question.dependent_questions) {
      setSelectedOptions({
        [value]: question.dependent_questions[value] || [],
      });
    }
  };

  const handleOptionChange = (option) => {
    setCurrentValue(option.label);
    setSelectedOptions({ [option.label]: option.dependent_questions || [] });
  };

  const handleCheckboxChange = (option, isChecked) => {
    setSelectedOptions((prev) => {
      const updated = { ...prev };
      if (isChecked) {
        updated[option.label] = option.dependent_questions || [];
      } else {
        delete updated[option.label];
      }
      return updated;
    });
  };

  return (
    <div className="p-4 border rounded shadow-sm bg-gray-100">
      <label className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
        {question.text}
        {question.is_required && (
          <span className="text-red-500">
            <FaStar />
          </span>
        )}
      </label>
      {renderQuestionField(
        question,
        handleOptionChange,
        handleCheckboxChange,
        handleYesNoChange
      )}
      {/* Render Dependent Questions */}
      {Object.values(selectedOptions)
        .flat()
        .map((depQuestion, index) => (
          <div
            key={`${question.question_id}_dep_${index}`}
            className="mt-4 ml-4 border-l-4 pl-4 border-blue-400"
          >
            <QuestionWithDependencies question={depQuestion} />
          </div>
        ))}
    </div>
  );
};

const renderQuestionField = (
  question,
  handleOptionChange,
  handleCheckboxChange,
  handleYesNoChange
) => {
  switch (question.type) {
    case "text":
      return (
        <input
          type="text"
          className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Enter your response"
        />
      );
    case "number":
      return (
        <input
          type="number"
          className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Enter a number"
        />
      );
    case "yes_no":
      return (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id={`${question.question_id}_yes`}
              name={question.question_id}
              className="form-radio text-blue-500"
              onChange={() => handleYesNoChange("yes")}
            />
            <label
              htmlFor={`${question.question_id}_yes`}
              className="text-gray-700"
            >
              Yes
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id={`${question.question_id}_no`}
              name={question.question_id}
              className="form-radio text-blue-500"
              onChange={() => handleYesNoChange("no")}
            />
            <label
              htmlFor={`${question.question_id}_no`}
              className="text-gray-700"
            >
              No
            </label>
          </div>
        </div>
      );
    case "dropdown":
      return (
        <select
          className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          onChange={(e) =>
            handleOptionChange(
              question.options.find((option) => option.label === e.target.value)
            )
          }
        >
          <option value="">Select an option</option>
          {question.options.map((option, index) => (
            <option key={index} value={option.label}>
              {option.label}
            </option>
          ))}
        </select>
      );
    case "checkbox_group":
      return (
        <div className="space-y-2">
          {question.options.map((option, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`${question.question_id}_${index}`}
                className="form-checkbox text-blue-500"
                onChange={(e) => handleCheckboxChange(option, e.target.checked)}
              />
              <label
                htmlFor={`${question.question_id}_${index}`}
                className="text-gray-700"
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      );
    default:
      return <div className="text-gray-500">Unsupported field type</div>;
  }
};

export default FormPreview;
